import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../services/supabase';
import { FUNCTIONS_URL } from '../services/supabaseConfig';

interface LootReward {
  type: string;
  boost?: {
    id: string;
    name: string;
    target_attribute: string;
    boost_value: number;
      icon: string;
    forge_rank: number;
    player_context: string;
    boost_type: string;
  };
  ballsAmount?: number;
  isPlayerCreation?: boolean;
  message: string;
  claimedAt: string;
}

interface LootBoxPopperProps {
  userData: any;
  onClaimed?: () => void;
}

const ATTR_ICONS: Record<string, string> = {
  power: '💪',
  contact: '🎯',
  speed: '⚡',
  fielding: '🧤',
  arm: '🦾',
  discipline: '🧠',
  endurance: '🫀',
  velocity: '🔥',
  control: '🎯',
  movement: '🌀',
  deception: '🎭',
  stamina: '🔋',
};

export default function LootBoxPopper({ userData, onClaimed }: LootBoxPopperProps) {
  const [canClaim, setCanClaim] = useState<boolean | null>(null);
  const [isOpening, setIsOpening] = useState(false);
  const [reward, setReward] = useState<LootReward | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [particles, setParticles] = useState<Array<{ id: number; x: number; y: number; color: string; size: number; angle: number; speed: number; rotation: number }>>([]);
  const [shakeCount, setShakeCount] = useState(0);

  // Check if user can claim
  useEffect(() => {
    if (!userData?.id) return;
    checkDaily();
  }, [userData?.id]);

  async function checkDaily() {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        // No session — default to showing the box (will prompt sign-in on tap)
        setCanClaim(true);
        return;
      }

      const res = await fetch(`${FUNCTIONS_URL}/daily-reward`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ action: 'check-daily' }),
      });

      const data = await res.json();
      if (data.claimed) {
        setCanClaim(false);
        setReward(data.reward);
      } else {
        setCanClaim(true);
      }
    } catch (e) {
      console.error('Check daily error:', e);
      // On error, show the box anyway — better than hiding it
      setCanClaim(true);
    }
  }

  // Generate particles for the pop effect
  const generateParticles = useCallback((color: string) => {
    const newParticles = Array.from({ length: 40 }, (_, i) => ({
      id: Date.now() + i,
      x: 0,
      y: 0,
      color: i % 3 === 0 ? '#eab308' : i % 3 === 1 ? color : '#fff',
      size: Math.random() * 8 + 3,
      angle: (Math.PI * 2 * i) / 40 + (Math.random() - 0.5) * 0.5,
      speed: Math.random() * 200 + 100,
      rotation: Math.random() * 360,
    }));
    setParticles(newParticles);
    // Clear particles after animation
    setTimeout(() => setParticles([]), 1000);
  }, []);

  // Shake animation before pop
  async function handleClaim() {
    if (!canClaim || isOpening) return;
    setIsOpening(true);
    setShowResult(false);

    // Shake phase: 3 quick shakes
    for (let i = 0; i < 6; i++) {
      await new Promise(r => setTimeout(r, 80));
      setShakeCount(i);
    }

    // Pop! Call the API
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const res = await fetch(`${FUNCTIONS_URL}/daily-reward`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ action: 'claim-daily' }),
      });

      const data = await res.json();
      if (data.success) {
        const newReward = data.reward;
        setReward(newReward);
        setCanClaim(false);

        // Generate particles with the rarity color
        generateParticles('#f59e0b');

        // Short delay then show result
        setTimeout(() => {
          setShowResult(true);
          setIsOpening(false);
          onClaimed?.();
        }, 300);
      } else {
        setIsOpening(false);
        if (data.error?.includes('Already claimed')) {
          setCanClaim(false);
        }
      }
    } catch (e) {
      console.error('Claim error:', e);
      setIsOpening(false);
    }
  }

  if (canClaim === null) return null;

  // Already claimed today — show a small indicator
  if (canClaim === false && !showResult && reward) {
    return (
      <div style={{
        background: 'linear-gradient(135deg, rgba(15,20,35,0.95), rgba(20,28,50,0.95))',
        border: '1px solid rgba(201,149,26,0.2)',
        borderRadius: 12,
        padding: '16px 20px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
      }}>
        <div style={{ fontSize: 28 }}>🎁</div>
        <div>
          <div style={{ color: '#9ca3af', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
            Daily Reward Claimed
          </div>
          <div style={{ color: reward.boost ? '#f59e0b' : '#d4a017', fontSize: 14, marginTop: 2 }}>
            {reward.boost ? reward.boost.name : reward.isPlayerCreation ? 'Free Player Creation!' : reward.message || 'Check back tomorrow!'}
          </div>
        </div>
      </div>
    );
  }

  // Can't claim and no reward (edge case)
  if (canClaim === false && !reward) return null;

  return (
    <div style={{ position: 'relative' }}>
      {/* Particle container */}
      {particles.length > 0 && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          zIndex: 50,
          pointerEvents: 'none',
        }}>
          {particles.map((p) => (
            <div
              key={p.id}
              style={{
                position: 'absolute',
                width: p.size,
                height: p.size,
                background: p.color,
                borderRadius: p.size > 6 ? '50%' : '2px',
                transform: `rotate(${p.rotation}deg)`,
                animation: `particle-fly-${p.id % 8} 0.8s ease-out forwards`,
                left: Math.cos(p.angle) * p.speed * 0.8,
                top: Math.sin(p.angle) * p.speed * 0.8,
                boxShadow: `0 0 6px ${p.color}`,
              }}
            />
          ))}
        </div>
      )}

      {/* Main loot box */}
      <div
        onClick={handleClaim}
        style={{
          background: isOpening
            ? 'linear-gradient(135deg, rgba(234,179,8,0.2), rgba(201,149,26,0.15))'
            : 'linear-gradient(135deg, rgba(15,20,35,0.95), rgba(20,28,50,0.95))',
          border: isOpening
            ? '2px solid rgba(234,179,8,0.6)'
            : '2px solid rgba(201,149,26,0.3)',
          borderRadius: 16,
          padding: '24px 32px',
          cursor: canClaim && !isOpening ? 'pointer' : 'default',
          transition: 'all 0.2s ease',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
          transform: isOpening
            ? `translateX(${shakeCount % 2 === 0 ? -4 : 4}px) scale(${isOpening ? 1.05 : 1})`
            : 'none',
          boxShadow: isOpening
            ? '0 0 40px rgba(234,179,8,0.4)'
            : '0 4px 20px rgba(0,0,0,0.3)',
          userSelect: 'none',
        }}
      >
        {/* Ambient glow behind box */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: 200,
          height: 200,
          transform: 'translate(-50%, -50%)',
          background: 'radial-gradient(circle, rgba(234,179,8,0.1) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />

        {!showResult ? (
          // Before pop — show the loot box
          <>
            <div style={{
              fontSize: 48,
              marginBottom: 8,
              animation: isOpening ? 'pop-shake 0.1s ease-in-out infinite' : 'loot-bob 2s ease-in-out infinite',
              filter: isOpening ? 'brightness(1.5)' : 'none',
            }}>
              {isOpening ? '💥' : '🎁'}
            </div>
            <div style={{
              color: '#e8b82a',
              fontSize: 18,
              fontWeight: 700,
              letterSpacing: 1,
              textTransform: 'uppercase',
            }}>
              {isOpening ? 'Opening...' : 'Daily Loot Box'}
            </div>
            <div style={{
              color: '#9ca3af',
              fontSize: 13,
              marginTop: 4,
            }}>
              {isOpening ? '' : 'Tap to open!'}
            </div>


          </>
        ) : (
          // After pop — show the reward
          <div style={{
            animation: 'reward-appear 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards',
          }}>


            {reward?.type === 'player_creation' ? (
              // Epic — Free Player Creation
              <div style={{
                animation: 'legendary-pulse 1.5s ease-in-out infinite',
              }}>
                <div style={{ fontSize: 48, marginBottom: 8 }}>⭐</div>
                <div style={{
                  color: '#a855f7',
                  fontSize: 22,
                  fontWeight: 800,
                  textShadow: '0 0 20px rgba(168,85,247,0.5)',
                }}>
                  FREE PLAYER CREATION!
                </div>
                <div style={{ color: '#9ca3af', fontSize: 13, marginTop: 4 }}>
                  Use it in the Agent Office
                </div>
              </div>
            ) : reward?.type === 'balls' ? (
              // BALLS reward
              <div>
                <div style={{ fontSize: 48, marginBottom: 8 }}>🪙</div>
                <div style={{
                  color: '#e8b82a',
                  fontSize: 22,
                  fontWeight: 800,
                }}>
                  +{reward.ballsAmount} BALLS
                </div>
              </div>
            ) : (
              // Boost reward
              <div>
                <div style={{
                  fontSize: 44,
                  marginBottom: 8,
                  filter: `drop-shadow(0 0 12px ${'#f59e0b'}66)`,
                }}>
                  {reward?.boost ? (ATTR_ICONS[reward.boost.target_attribute] || '⬆️') : '⬆️'}
                </div>
                <div style={{
                  color: '#f59e0b',
                  fontSize: 20,
                  fontWeight: 800,
                  textShadow: `0 0 15px ${'#f59e0b'}44`,
                }}>
                  {reward?.boost?.name || 'Boost'}
                </div>
                {reward?.boost?.player_context && reward.boost.player_context !== 'any' && (
                  <div style={{
                    color: '#9ca3af',
                    fontSize: 12,
                    marginTop: 4,
                    textTransform: 'capitalize',
                  }}>
                    {reward.boost.player_context === 'hitter' ? '⚾ Hitter Only' : '🎯 Pitcher Only'}
                  </div>
                )}
              </div>
            )}

            <div style={{
              color: '#6b7280',
              fontSize: 11,
              marginTop: 12,
            }}>
              <span onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: '/my-teams' }))} style={{ color: '#d4a017', cursor: 'pointer', textDecoration: 'underline' }}>
                View in Owner's Office →
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Inline keyframes — must be in the DOM */}
      <style>{`
        @keyframes loot-bob {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
        @keyframes pop-shake {
          0%, 100% { transform: translateX(0) rotate(0deg); }
          25% { transform: translateX(-3px) rotate(-2deg); }
          75% { transform: translateX(3px) rotate(2deg); }
        }
        @keyframes reward-appear {
          0% { opacity: 0; transform: scale(0.3); }
          60% { transform: scale(1.1); }
          100% { opacity: 1; transform: scale(1); }
        }
        @keyframes legendary-pulse {
          0%, 100% { filter: brightness(1); }
          50% { filter: brightness(1.3); }
        }
        @keyframes particle-fly-0 {
          0% { opacity: 1; transform: translate(0, 0) scale(1); }
          100% { opacity: 0; transform: translate(40px, -60px) scale(0); }
        }
        @keyframes particle-fly-1 {
          0% { opacity: 1; transform: translate(0, 0) scale(1); }
          100% { opacity: 0; transform: translate(60px, -30px) scale(0); }
        }
        @keyframes particle-fly-2 {
          0% { opacity: 1; transform: translate(0, 0) scale(1); }
          100% { opacity: 0; transform: translate(70px, 10px) scale(0); }
        }
        @keyframes particle-fly-3 {
          0% { opacity: 1; transform: translate(0, 0) scale(1); }
          100% { opacity: 0; transform: translate(50px, 50px) scale(0); }
        }
        @keyframes particle-fly-4 {
          0% { opacity: 1; transform: translate(0, 0) scale(1); }
          100% { opacity: 0; transform: translate(-40px, -60px) scale(0); }
        }
        @keyframes particle-fly-5 {
          0% { opacity: 1; transform: translate(0, 0) scale(1); }
          100% { opacity: 0; transform: translate(-60px, -20px) scale(0); }
        }
        @keyframes particle-fly-6 {
          0% { opacity: 1; transform: translate(0, 0) scale(1); }
          100% { opacity: 0; transform: translate(-50px, 40px) scale(0); }
        }
        @keyframes particle-fly-7 {
          0% { opacity: 1; transform: translate(0, 0) scale(1); }
          100% { opacity: 0; transform: translate(-20px, 60px) scale(0); }
        }
      `}</style>
    </div>
  );
}
